package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.style.TextAlign
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCount
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_copy_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_like_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_more_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_save_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_share_a11y
import org.jetbrains.compose.resources.stringResource

/**
 * The full-bleed "story card" shared by the Jokes and Danestaniha swipe readers: a rich brand
 * gradient ([AppTheme.gradients]), a big faded ornament watermark, large centered white text with
 * an optional source line, a frosted category chip, a page counter, and a frosted end-edge action
 * rail (like / save / copy / share / more). Double-tap the card to like. All white-on-gradient so
 * it reads as one shareable, modern quote card; start/end layout mirrors correctly in RTL.
 */
@Composable
internal fun GemStoryCard(
    key: String,
    gradient: Brush,
    watermark: String,
    counter: String,
    categoryLabel: String,
    text: String,
    likeCount: Int,
    liked: Boolean,
    saved: Boolean,
    onDoubleTapLike: () -> Unit,
    onToggleLike: () -> Unit,
    onToggleSave: () -> Unit,
    onCopy: () -> Unit,
    onShare: () -> Unit,
    onMore: () -> Unit,
    modifier: Modifier = Modifier,
    source: String? = null,
) {
    val onGradient = AppTheme.colors.onPrimary // white
    Box(
        modifier = modifier
            .fillMaxSize()
            .padding(horizontal = AppTheme.spacing.lg, vertical = AppTheme.spacing.md),
        contentAlignment = Alignment.Center,
    ) {
        Box(
            modifier = Modifier
                .widthIn(max = AppTheme.sizes.contentMaxWidthCompact)
                .fillMaxSize()
                .clip(AppTheme.shapes.card)
                .background(gradient)
                .pointerInput(key) { detectTapGestures(onDoubleTap = { onDoubleTapLike() }) },
        ) {
            // Oversized faded ornament, tucked into the top-end corner behind the text.
            AppIcon(
                name = watermark,
                size = AppTheme.sizes.gemWatermark,
                tint = onGradient.copy(alpha = 0.14f),
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(AppTheme.spacing.sm),
            )
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(AppTheme.spacing.xl)
                    // Reserve the end-edge rail's width so long text never runs under the actions.
                    .padding(end = AppTheme.sizes.minTouchTarget)
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(
                    text = counter,
                    style = AppTheme.typography.labelSm,
                    color = onGradient.copy(alpha = 0.75f),
                    modifier = Modifier.align(Alignment.End),
                )
                Spacer(modifier = Modifier.weight(1f))
                Text(
                    text = text,
                    style = AppTheme.typography.headlineLgMobile,
                    color = onGradient,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.padding(vertical = AppTheme.spacing.lg),
                )
                if (source != null) {
                    Text(
                        text = source,
                        style = AppTheme.typography.labelSm,
                        color = onGradient.copy(alpha = 0.8f),
                        textAlign = TextAlign.Center,
                        modifier = Modifier.padding(bottom = AppTheme.spacing.md),
                    )
                }
                GlassChip(label = categoryLabel, content = onGradient)
                Spacer(modifier = Modifier.weight(1f))
            }
            GemActionRail(
                likeCount = likeCount,
                liked = liked,
                saved = saved,
                content = onGradient,
                onToggleLike = onToggleLike,
                onToggleSave = onToggleSave,
                onCopy = onCopy,
                onShare = onShare,
                onMore = onMore,
                modifier = Modifier
                    .align(Alignment.CenterEnd)
                    .padding(end = AppTheme.spacing.sm),
            )
        }
    }
}

/** A frosted (translucent white) pill chip for use on top of a gradient. */
@Composable
private fun GlassChip(label: String, content: androidx.compose.ui.graphics.Color) {
    Text(
        text = label,
        style = AppTheme.typography.labelMd,
        color = content,
        modifier = Modifier
            .background(content.copy(alpha = 0.18f), AppTheme.shapes.pill)
            .padding(horizontal = AppTheme.spacing.md, vertical = AppTheme.spacing.xs),
    )
}

/** End-edge frosted action rail (mirrors in RTL). */
@Composable
private fun GemActionRail(
    likeCount: Int,
    liked: Boolean,
    saved: Boolean,
    content: androidx.compose.ui.graphics.Color,
    onToggleLike: () -> Unit,
    onToggleSave: () -> Unit,
    onCopy: () -> Unit,
    onShare: () -> Unit,
    onMore: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val lang = AppTheme.language
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
    ) {
        GlassIconButton(
            iconName = "favorite",
            contentDescription = stringResource(Res.string.gems_like_a11y),
            content = content,
            filled = liked,
            onClick = onToggleLike,
        )
        Text(
            text = lang.formatCount(likeCount),
            style = AppTheme.typography.labelSm,
            color = content,
        )
        GlassIconButton(
            iconName = "bookmark",
            contentDescription = stringResource(Res.string.gems_save_a11y),
            content = content,
            filled = saved,
            onClick = onToggleSave,
        )
        GlassIconButton(iconName = "content_copy", contentDescription = stringResource(Res.string.gems_copy_a11y), content = content, onClick = onCopy)
        GlassIconButton(iconName = "share", contentDescription = stringResource(Res.string.gems_share_a11y), content = content, onClick = onShare)
        GlassIconButton(iconName = "more_vert", contentDescription = stringResource(Res.string.gems_more_a11y), content = content, onClick = onMore)
    }
}

/** One frosted circular icon button (white icon on a translucent-white disc). */
@Composable
private fun GlassIconButton(
    iconName: String,
    contentDescription: String?,
    content: androidx.compose.ui.graphics.Color,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    filled: Boolean = false,
) {
    Box(
        modifier = modifier
            .size(AppTheme.sizes.gemGlassButton)
            .clip(CircleShape)
            .background(content.copy(alpha = if (filled) 0.28f else 0.16f))
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center,
    ) {
        AppIcon(
            name = iconName,
            filled = filled,
            size = AppTheme.sizes.iconButton,
            tint = content,
            contentDescription = contentDescription,
        )
    }
}
